// Google Analytics 4 wrapper.
//
// Single source of truth for all analytics calls. Centralised here so:
//  - GA only initialises in production (gated by REACT_APP_ENV === 'prod' and a
//    measurement ID being present)
//  - calls are no-ops in dev/test, no need for `if (env)` checks at call sites
//  - we never accidentally send PII (we use the Entra `localAccountId` GUID as
//    the GA user_id; never email or display name)
//
// Public API:
//   initAnalytics()          // call once at app boot
//   setUserId(oid)           // when auth resolves
//   clearUserId()            // on logout
//   trackPageView(path, title)
//   trackEvent(name, params) // GA4 recommended event names where possible
//   reportWebVitals()        // wires web-vitals -> GA4 events

import ReactGA from 'react-ga4';

const GA_ID = process.env.REACT_APP_GA_ID;
const ENV = process.env.REACT_APP_ENV;
const ENABLED = ENV === 'prod' && !!GA_ID;

let initialized = false;

export function initAnalytics() {
  if (!ENABLED || initialized) return;
  ReactGA.initialize(GA_ID, {
    gaOptions: {
      // We send page_view manually from RouteTracker so SPA navigations are
      // captured. Without this, GA4 only counts the first load as a pageview.
      send_page_view: false,
      // GA4 anonymises IP by default; setting it explicitly is documentation.
      anonymize_ip: true,
    },
  });
  initialized = true;
}

export function setUserId(oid) {
  if (!ENABLED || !oid) return;
  ReactGA.set({ user_id: oid });
}

export function clearUserId() {
  if (!ENABLED) return;
  ReactGA.set({ user_id: null });
}

export function trackPageView(path, title) {
  if (!ENABLED) return;
  ReactGA.send({ hitType: 'pageview', page: path, title });
}

export function trackEvent(name, params = {}) {
  if (!ENABLED) return;
  ReactGA.event(name, params);
}

// ── Web Vitals → GA4 ────────────────────────────────────────────────────────
// Each metric becomes a `web_vitals` event so they aggregate in one place.
// Use Explorations in GA4 with `metric_name` as a dimension to compare LCP /
// CLS / INP / FCP / TTFB. Values are in ms (or unitless for CLS).
export function reportWebVitals() {
  if (!ENABLED) return;
  // Lazy-load so the metric collection code is not in the main bundle path.
  import('web-vitals').then(({ getCLS, getFCP, getFID, getLCP, getTTFB }) => {
    const send = (metric) => {
      ReactGA.event('web_vitals', {
        metric_name: metric.name,
        metric_id: metric.id,
        // GA prefers integer values; CLS is fractional so multiply by 1000.
        metric_value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
        metric_delta: metric.delta,
        metric_rating: metric.rating, // 'good' | 'needs-improvement' | 'poor'
      });
    };
    getCLS(send);
    getFCP(send);
    getFID(send);
    getLCP(send);
    getTTFB(send);
  });
}
